/**
 * Theme tokens for the /support marketing page.
 *
 * Light mode mirrors the editorial marketing aesthetic used across the other
 * marketing pages (neutral paper, ink text, cobalt accent). Dark mode mirrors
 * the cinematic dark tone. Resolved from `next-themes` in SupportPage and
 * threaded down so the hero, category grid, callouts, and ticket form all share
 * one source of truth.
 */
export type SupportTone = {
  /** Page background + base text. */
  page: string;
  /** Mono uppercase eyebrow/kicker label. */
  kicker: string;
  /** Display heading ink. */
  heading: string;
  /** Italic accent within headings. */
  accent: string;
  /** Body / supporting copy. */
  body: string;
  faint: string;
  /** Hairline divider border. */
  divider: string;
  /** Outer wrapper for the gap-px card grid (acts as the divider color). */
  gridShell: string;
  /** Card surface. */
  card: string;
  cardTitle: string;
  cardBody: string;
  /** Category icon chip. */
  iconChip: string;
  /** "What happens next" callout. */
  callout: string;
  calloutStrong: string;
  calloutBody: string;
  /** Trust pills. */
  pill: string;
  /** Quick-link rows. */
  quickLink: string;
  /** Form panel surface + border. */
  panel: string;
  panelBorder: string;
  /** Form field label. */
  label: string;
  /** Form input/textarea. */
  input: string;
  /** Sign-in gate panel. */
  gate: string;
  /** Primary (submit) button. */
  btnPrimary: string;
  /** Secondary button (sign in). */
  btnSecondary: string;
  /** Success banner. */
  success: string;
  successIcon: string;
  successTitle: string;
  successBody: string;
  /** Error banner. */
  error: string;
  errorIcon: string;
  errorTitle: string;
  errorBody: string;
};

const LIGHT: SupportTone = {
  page: "bg-[var(--zom-warm-white)] text-[var(--zom-graphite)]",
  kicker: "text-[color:var(--zom-accent-primary)]",
  heading: "text-[var(--zom-graphite)]",
  accent: "text-[color:var(--zom-accent-primary)]",
  body: "text-[var(--zom-gunmetal)]",
  faint: "text-[var(--zom-gunmetal)]",
  divider: "border-[var(--border)]",
  gridShell: "border-[var(--border)] bg-[var(--border)]",
  card: "bg-[color:var(--zom-public-panel-bg)]",
  cardTitle: "text-[var(--zom-graphite)]",
  cardBody: "text-[var(--zom-gunmetal)]",
  iconChip:
    "border border-[var(--border)] bg-[var(--zom-warm-white)] text-[var(--zom-gunmetal)]",
  callout:
    "border-[color:var(--zom-cobalt-100)] bg-[color:var(--zom-cobalt-50)]",
  calloutStrong: "text-[var(--zom-graphite)]",
  calloutBody: "text-[color:var(--zom-cobalt-600)]",
  pill: "border-[var(--border)] bg-[color:var(--zom-public-panel-bg)] text-[var(--zom-gunmetal)]",
  quickLink:
    "border-[var(--border)] text-[var(--zom-gunmetal)] hover:border-[var(--border)] hover:text-[var(--zom-graphite)]",
  panel: "bg-[color:var(--zom-public-panel-bg)]",
  panelBorder: "border-[var(--border)]",
  label: "text-[var(--zom-gunmetal)]",
  input:
    "border-[var(--border)] bg-[var(--zom-warm-white)] text-[var(--zom-graphite)] placeholder:text-[var(--zom-gunmetal)] focus:border-[color:var(--zom-accent-primary)] focus:bg-[color:var(--zom-public-panel-bg)] focus:ring-[color:var(--zom-public-accent-ring)]",
  gate: "border-[var(--border)] bg-[var(--zom-warm-white)]",
  btnPrimary:
    "bg-[var(--zom-graphite)] text-[var(--zom-warm-white)] hover:bg-[var(--zom-graphite)]",
  btnSecondary:
    "border border-[var(--border)] bg-[color:var(--zom-public-panel-bg)] text-[var(--zom-gunmetal)] hover:border-[var(--border)] hover:text-[var(--zom-graphite)]",
  success: "border-[var(--zom-gain-soft)] bg-[var(--zom-gain-soft)]",
  successIcon: "text-[var(--zom-gain)]",
  successTitle: "text-[var(--zom-gain)]",
  successBody: "text-[var(--zom-gain)]",
  error: "border-[var(--zom-loss-soft)] bg-[var(--zom-loss-soft)]",
  errorIcon: "text-[var(--destructive)]",
  errorTitle: "text-[var(--destructive)]",
  errorBody: "text-[var(--destructive)]",
};

const DARK: SupportTone = {
  page: "bg-[var(--background)] text-[color:var(--zom-public-white)]",
  kicker: "text-[color:var(--zom-cobalt-400)]",
  heading: "text-[color:var(--zom-public-white)]",
  accent: "text-[color:var(--zom-cobalt-400)]",
  body: "text-[color:var(--zom-public-white-copy)]",
  faint: "text-[color:var(--zom-public-white-subtle)]",
  divider: "border-[color:var(--zom-public-white-hairline)]",
  gridShell:
    "border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-hairline)]",
  card: "bg-[color:var(--zom-public-white-panel)]",
  cardTitle: "text-[color:var(--zom-public-white)]",
  cardBody: "text-[color:var(--zom-public-white-soft)]",
  iconChip:
    "bg-[color:var(--zom-public-accent-bg-strong)] text-[color:var(--zom-cobalt-400)]",
  callout:
    "border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-wash)]",
  calloutStrong: "text-[color:var(--zom-public-white)]",
  calloutBody: "text-[color:var(--zom-public-white-copy)]",
  pill: "border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-wash)] text-[color:var(--zom-public-white-muted)]",
  quickLink:
    "border-[color:var(--zom-public-white-hairline)] text-[color:var(--zom-public-white-muted)] hover:border-[color:var(--zom-public-glass-border-strong)] hover:text-[color:var(--zom-public-white)]",
  panel: "bg-[color:var(--zom-public-white-panel-muted)]",
  panelBorder: "border-[color:var(--zom-public-white-hairline)]",
  label: "text-[color:var(--zom-public-white-faint)]",
  input:
    "border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-wash)] text-[color:var(--zom-public-white)] placeholder:text-[color:var(--zom-public-white-faint)] focus:border-[color:var(--zom-cobalt-400)] focus:bg-[color:var(--zom-public-white-panel)] focus:ring-[color:var(--zom-public-accent-ring)]",
  gate: "border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-wash)]",
  btnPrimary:
    "bg-[color:var(--zom-on-accent)] text-[color:var(--zom-rich-black)] hover:bg-[color:var(--zom-public-white)]",
  btnSecondary:
    "border border-[color:var(--zom-public-white-hairline)] bg-[color:var(--zom-public-white-wash)] text-[color:var(--zom-public-white-muted)] hover:border-[color:var(--zom-public-glass-border-strong)] hover:text-[color:var(--zom-public-white)]",
  success:
    "border-[color:var(--zom-gain-border)] bg-[color:var(--zom-gain-soft)]",
  successIcon: "text-[color:var(--zom-gain)]",
  successTitle: "text-[color:var(--zom-gain)]",
  successBody: "text-[color:var(--zom-gain)]",
  error:
    "border-[color:var(--zom-loss-border)] bg-[color:var(--zom-loss-soft)]",
  errorIcon: "text-[color:var(--destructive)]",
  errorTitle: "text-[color:var(--destructive)]",
  errorBody: "text-[color:var(--destructive)]",
};

export function getSupportTone(isDark: boolean): SupportTone {
  return isDark ? DARK : LIGHT;
}
